import FilesAPI from 'api/FilesAPI';
import { AxiosResponse } from 'axios';
import { JAVA_SERVICE_ENDPOINTS } from '@buildwise/libs/services/endpoints';
import useUserData from 'hooks/useUserData';
import _ from 'lodash';
import { useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';

export type FileType = {
  UploadUrl?: string;
  companyId?: string;
  createdAt?: string | Date;
  createdBy?: string;
  documentDirectory: string;
  documentName: string;
  documentPDF?: string;
  documentPath?: string;
  documentSize: number;
  documentType: string;
  documentURL: string;
  file?: File;
  fileMD5Hash?: string;
  generatePreSignedURLFlag?: boolean;
  id: string;
  isDocumentUploaded?: boolean;
  profileImageURL?: string;
  projectId?: string;
  s3Key?: string;
  userNameExpYearsToDisplay: string;
  userNameTitleToDisplay: string;
  userNameToDisplay: string;
};

function useFilesQuery({
  level,
  projectId,
  shouldFetch = true,
}: {
  level: 'company' | 'project';
  projectId?: string;
  shouldFetch?: boolean;
}) {
  const { companyId } = useUserData();
  const queryClient = useQueryClient();
  const { data: filesData, ...rest } = useQuery<AxiosResponse<{ documents: FileType[] }>, Error>(
    [JAVA_SERVICE_ENDPOINTS.COMPANY_FILES, companyId, level, projectId],
    () => FilesAPI.getFiles(companyId, level, projectId),
    {
      staleTime: 2 * 60 * 1000,
      refetchOnMount: 'always',
      keepPreviousData: true,
      enabled: !!companyId && shouldFetch,
    },
  );

  const files = useMemo(
    () =>
      Array.isArray(filesData?.data?.documents)
        ? _.orderBy(filesData?.data?.documents || [], 'createdAt', 'desc')
        : [],
    [filesData?.data],
  );

  const updateFilesCache = ({
    action,
    files = [],
    removeFileIds = [],
  }: {
    action: 'add' | 'remove';
    files?: FileType[];
    removeFileIds?: string[];
  }) => {
    const queryStatus = queryClient.getQueryState([
      JAVA_SERVICE_ENDPOINTS.COMPANY_FILES,
      companyId,
      level,
      projectId,
    ]);

    if (queryStatus?.status && queryStatus.status !== 'idle') {
      queryClient.setQueriesData<AxiosResponse<{ documents: FileType[] }> | undefined>(
        [JAVA_SERVICE_ENDPOINTS.COMPANY_FILES, companyId, level, projectId],
        (old) => {
          if (old) {
            let updatedDocuments = [...(old?.data?.documents || [])];

            if (action === 'add' && files.length) {
              updatedDocuments.push(...files);
            }

            if (action === 'remove') {
              updatedDocuments = updatedDocuments.filter(
                (file) => !removeFileIds.includes(file.id),
              );
            }

            return {
              ...old,
              data: {
                ...old.data,
                documents: updatedDocuments,
              },
            };
          }

          return old;
        },
      );
    }
  };

  return {
    files,
    ...rest,
    updateFilesCache,
  };
}

export default useFilesQuery;
